import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Search, Edit2, FileText, Paperclip, AlertTriangle,
  Info, Download, FileSpreadsheet, CheckCircle2, XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { expensesAPI, downloadFile } from '../../services/api';
import { formatEuro } from '../../services/serviceCatalog';
import {
  EXPENSE_GROUPS, categoryLabel, paymentMethodLabel,
} from '../../services/expenseCategories';
import LoadingSpinner from '../Common/LoadingSpinner';
import ExpenseForm from './ExpenseForm';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import './Expenses.css';

const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const STATUS_OPTIONS = [
  { value: 'active',     label: 'Aktiv' },
  { value: 'storniert',  label: 'Storniert' },
  { value: 'all',        label: 'Alle' },
];

const Expenses = () => {
  const today = useMemo(() => new Date(), []);
  const [expenses, setExpenses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(''); // '' = all months
  const [statusFilter, setStatusFilter] = useState('active');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const years = useMemo(() => {
    const out = [];
    for (let y = today.getFullYear() + 1; y >= today.getFullYear() - 5; y--) out.push(y);
    return out;
  }, [today]);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const params = {
        status: statusFilter,
        search: search || undefined,
        category: category || undefined,
        year: year || undefined,
        month: month || undefined,
      };
      const { data } = await expensesAPI.getAll(params);
      setExpenses(data);
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await expensesAPI.getStats();
      setStats(data);
    } catch {
      /* non-fatal */
    }
  };

  useEffect(() => { loadExpenses(); /* eslint-disable-next-line */ }, [search, category, year, month, statusFilter]);
  useEffect(() => { loadStats(); }, []);

  const formatDate = (s) => (s ? format(parseISO(s), 'dd.MM.yyyy', { locale: de }) : '—');

  const handleEdit = (exp) => { setEditing(exp); setFormOpen(true); };
  const handleNew  = ()    => { setEditing(null); setFormOpen(true); };
  const handleSaved = () => {
    setFormOpen(false);
    setEditing(null);
    loadExpenses();
    loadStats();
  };

  const handleStorno = async (exp) => {
    const reason = window.prompt(
      `Grund für Storno von "${exp.description}" (optional):`
    );
    if (reason === null) return;
    try {
      setBusyId(exp.id);
      await expensesAPI.storno(exp.id, reason.trim() || null);
      toast.success('Storniert');
      loadExpenses(); loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Storno fehlgeschlagen');
    } finally { setBusyId(null); }
  };

  const handleDownloadReceipt = async (exp) => {
    if (!exp.receipt_path) return;
    try {
      setBusyId(exp.id);
      const res = await expensesAPI.downloadReceipt(exp.id);
      downloadFile(res.data, exp.receipt_original_name || `Beleg_${exp.id}`);
    } catch {
      toast.error('Beleg-Download fehlgeschlagen');
    } finally { setBusyId(null); }
  };

  const handleExport = async (format) => {
    try {
      const params = {
        format,
        kind: month ? 'month' : 'year',
        year,
        month: month || undefined,
      };
      const res = await expensesAPI.export(params);
      const suffix = month ? `${year}-${String(month).padStart(2,'0')}` : `${year}`;
      const name = format === 'datev'
        ? `DATEV_Ausgaben_${suffix}.csv`
        : `Ausgaben_${suffix}.csv`;
      downloadFile(res.data, name);
      toast.success('Export erstellt');
    } catch {
      toast.error('Export fehlgeschlagen');
    }
  };

  return (
    <div>
      <div className="content-header">
        <div>
          <h1>Ausgaben</h1>
          <p>Betriebsausgaben · Belege 10 Jahre aufbewahren (§147 AO)</p>
        </div>
        <button className="btn btn-primary" onClick={handleNew}>
          <Plus size={16} /> Neue Ausgabe
        </button>
      </div>

      <div className="content-body">
        {stats && (
          <div className="expense-summary">
            <div className="expense-kpi month">
              <div className="label">Dieser Monat</div>
              <div className="value">{formatEuro(stats.month_total)}</div>
              <div className="sub">{MONTHS[today.getMonth()]} {today.getFullYear()}</div>
            </div>
            <div className="expense-kpi year">
              <div className="label">Dieses Jahr</div>
              <div className="value">{formatEuro(stats.year_total)}</div>
              <div className="sub">{today.getFullYear()}</div>
            </div>
            {stats.top_categories?.slice(0, 1).map((c) => (
              <div className="expense-kpi top" key={c.key}>
                <div className="label">Top-Kategorie (Jahr)</div>
                <div className="value">{formatEuro(c.total)}</div>
                <div className="sub">{c.label} · {c.count}×</div>
              </div>
            ))}
          </div>
        )}

        <div className="legal-note">
          <Info size={18} />
          <div>
            <b>Aufbewahrungspflicht:</b> Nach §147 AO müssen Belege 10 Jahre aufbewahrt werden.
            Ausgaben können nicht gelöscht, nur <b>storniert</b> werden — der Eintrag bleibt
            sichtbar, wird aber als ungültig markiert.
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <div className="expenses-filters">
              <label>
                <span>Suche</span>
                <div className="relative">
                  <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    className="form-input pl-10"
                    placeholder="Beschreibung, Belegnr., Notizen…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </label>
              <label>
                <span>Kategorie</span>
                <select className="form-input" value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="">Alle</option>
                  {EXPENSE_GROUPS.map((g) => (
                    <optgroup key={g.key} label={g.label}>
                      {g.categories.map((c) => (
                        <option key={c.key} value={c.key}>{c.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
              <label>
                <span>Jahr</span>
                <select className="form-input" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </label>
              <label>
                <span>Monat</span>
                <select className="form-input" value={month} onChange={(e) => setMonth(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">Alle</option>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </label>
              <label>
                <span>Status</span>
                <select className="form-input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
            </div>
            <div className="flex gap-2 mt-3">
              <button className="btn btn-outline btn-sm" onClick={() => handleExport('datev')}>
                <Download size={14} /> DATEV-Export
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => handleExport('simple')}>
                <FileSpreadsheet size={14} /> Einfache CSV
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><LoadingSpinner size="large" /></div>
        ) : expenses.length === 0 ? (
          <div className="card">
            <div className="card-body text-center py-12">
              <FileText size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold mb-2">Keine Ausgaben</h3>
              <p className="text-gray-600 mb-6">
                Für die gewählten Filter wurden keine Ausgaben gefunden.
              </p>
              <button className="btn btn-primary" onClick={handleNew}>
                <Plus size={16} /> Erste Ausgabe erfassen
              </button>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Kategorie</th>
                  <th>Beschreibung</th>
                  <th>Zahlung</th>
                  <th>Beleg</th>
                  <th className="text-right">Betrag</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((exp) => {
                  const busy = busyId === exp.id;
                  const isStorno = exp.status === 'storniert';
                  return (
                    <tr key={exp.id} style={isStorno ? { opacity: 0.6 } : undefined}>
                      <td>{formatDate(exp.expense_date)}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{categoryLabel(exp.category)}</div>
                      </td>
                      <td>
                        {exp.description}
                        {exp.receipt_number && (
                          <div className="text-gray-500" style={{ fontSize: '0.78rem' }}>
                            Beleg-Nr.: {exp.receipt_number}
                          </div>
                        )}
                      </td>
                      <td>{paymentMethodLabel(exp.payment_method)}</td>
                      <td>
                        {exp.receipt_path ? (
                          <button
                            className="receipt-chip"
                            onClick={() => handleDownloadReceipt(exp)}
                            title="Beleg herunterladen"
                            disabled={busy}
                            style={{ border: 'none', cursor: 'pointer' }}
                          >
                            <Paperclip size={12} /> Vorhanden
                          </button>
                        ) : (
                          <span className="receipt-chip missing" title="Kein Beleg">
                            <AlertTriangle size={12} /> Fehlt
                          </span>
                        )}
                      </td>
                      <td className="text-right font-semibold">{formatEuro(exp.amount)}</td>
                      <td>
                        {isStorno ? (
                          <span className="storno-badge">Storniert</span>
                        ) : (
                          <span className="status-badge status-paid">
                            <CheckCircle2 size={12} style={{ marginRight: 4 }} />
                            Aktiv
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="table-actions">
                          {!isStorno && (
                            <>
                              <button
                                className="btn btn-outline btn-sm"
                                onClick={() => handleEdit(exp)}
                                disabled={busy}
                                title="Bearbeiten"
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleStorno(exp)}
                                disabled={busy}
                                title="Stornieren (nicht löschen!)"
                              >
                                <XCircle size={14} />
                              </button>
                            </>
                          )}
                          {isStorno && exp.storniert_reason && (
                            <span className="text-gray-500" style={{ fontSize: '0.75rem' }}>
                              {exp.storniert_reason}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formOpen && (
        <ExpenseForm
          initial={editing}
          onClose={() => { setFormOpen(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default Expenses;
